import { Box, Button, Checkbox, HStack, VStack } from '@chakra-ui/react'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { ViewMap, ViewMapChange, ViewMapProps } from './view-map'
import { CommunicatorContext } from '../../communication/communicator-context'

import _ from 'lodash'
import { web } from '@probe/shared/proto'
import { create } from '@bufbuild/protobuf'
import moment from 'moment'
import { DateRangeSlider } from '../date-range-slider'

type Span = {
    name: string
    span: () => [moment.Moment, moment.Moment]
}

const calculateSpans = (): Span[] => {
    const now = new Date()
    return [
        {
            name: '1 hr',
            span: () => [moment(), moment().subtract(1, 'hour')]
        },
        {
            name: '12 hrs',
            span: () => [moment(), moment().subtract(12, 'hours')]
        },
        {
            name: '24 hrs',
            span: () => [moment(), moment().subtract(24, 'hours')]
        },
        {
            name: '1 week',
            span: () => [moment(), moment().subtract(1, 'week')]
        },
        {
            name: '1 month',
            span: () => [moment(), moment().subtract(1, 'month')]
        },
        {
            name: '3 months',
            span: () => [moment(), moment().subtract(3, 'months')]
        }
    ]
}
type CastType = 'trolling' | 'static' | 'manual'
const allTypes: CastType[] = ['trolling', 'static', 'manual']

export function CastMap() {
    const communicator = useContext(CommunicatorContext)
    const [data, setData] = useState<ViewMapProps['data']>()
    const [fromDate, setFromDate] = useState<Date>(moment().subtract(7, 'days').toDate())
    const [toDate, setToDate] = useState<Date>(moment().toDate())
    const [change, setChange] = useState<ViewMapChange | undefined>()
    const [totals, setTotals] = useState<Record<string, number> | undefined>(undefined)

    const [selectedTypes, setSelectedTypes] = useState<CastType[]>(['trolling', 'static'])

    const range = {
        start: moment().subtract(1, 'year').toDate(),
        stop: moment().toDate()
    }

    const onRange = useCallback(({ start, stop }: { start: Date; stop: Date }) => {
        setFromDate(start)
        setToDate(stop)
    }, [])

    const mapsService = useMemo(() => communicator.rpcClient(web.v1.MapService), [communicator])

    useEffect(() => {
        if (fromDate && toDate && change) {
            const execute = async () => {
                const response = await mapsService.getCasts(
                    create(web.v1.GetCastsRequestSchema, {
                        types: selectedTypes.map((t) => {
                            if (t === 'static') {
                                return web.v1.CastType.STATIC
                            }
                            if (t === 'trolling') {
                                return web.v1.CastType.TROLLING
                            }
                            if (t === 'manual') {
                                return web.v1.CastType.MANUAL
                            }
                            throw new Error(`Unknown type ${t}`)
                        }),
                        date: {
                            start: Math.round(fromDate.getTime() / 1000),
                            end: Math.round(toDate.getTime() / 1000)
                        },
                        shape: (() => {
                            if (change.shape.type === 'bbox') {
                                return {
                                    case: 'bbox' as const,
                                    value: {
                                        minLng: change.shape.minLng,
                                        maxLng: change.shape.maxLng,
                                        minLat: change.shape.minLat,
                                        maxLat: change.shape.maxLat
                                    }
                                }
                            } else if (change.shape.type === 'wide') {
                                return {
                                    case: 'wide' as const,
                                    value: {
                                        minLat: change.shape.minLat,
                                        maxLat: change.shape.maxLat
                                    }
                                }
                            } else {
                                return {
                                    case: 'world' as const,
                                    value: {
                                        minLat: change.shape.minLat,
                                        maxLat: change.shape.maxLat
                                    }
                                }
                            }
                        })()
                    })
                )
                if (response.casts.case === 'aggregated') {
                    setTotals(
                        response.casts.value.entries.reduce(
                            (acc, c) => {
                                acc['trollings'] = (acc['trollings'] ?? 0) + c.trollings
                                acc['statics'] = (acc['statics'] ?? 0) + c.statics
                                acc['manuals'] = (acc['manuals'] ?? 0) + c.manuals
                                acc['installations'] = (acc['installations'] ?? 0) + c.installations
                                return acc
                            },
                            {} as Record<string, number>
                        )
                    )
                    setData({
                        type: 'aggregated',
                        casts: response.casts.value.entries.map((c) => ({
                            h3: c.h3,
                            installations: c.installations,
                            statics: c.statics,
                            trollings: c.trollings,
                            manuals: c.manuals
                        }))
                    })
                } else if (response.casts.case === 'raw') {
                    setTotals(undefined)
                    setData({
                        type: 'raw',
                        casts: response.casts.value.entries.map((c) => ({
                            long: c.long,
                            lat: c.lat,
                            timestamp: new Date((c.timestamp ?? 0) * 1000),
                            meta: {
                                depth: c.meta?.depth,
                                distance: c.meta?.distance,
                                duration: c.meta?.duration,
                                result: c.meta?.result
                            },
                            positions: c.cast
                                ? c.cast.positions?.positions.map((p) => ({
                                      long: p.long,
                                      lat: p.lat,
                                      distance: p.distanceCm / 100
                                  }))
                                : undefined
                        }))
                    })
                }
            }
            const t = setTimeout(execute, 300)
            return () => clearTimeout(t)
        }
    }, [fromDate, toDate, change, selectedTypes])

    return (
        <Box p={4} style={{ height: '100%' }}>
            <HStack gap={4} marginBottom={5} align="center" justify="flex-start">
                <DateRangeSlider
                    logTime
                    range={range}
                    start={fromDate}
                    stop={toDate}
                    onChange={onRange}
                />
                <HStack gap={4}>
                    {calculateSpans().map((s) => (
                        <Button
                            onClick={() => {
                                const [to, from] = s.span()
                                setFromDate(from.toDate())
                                setToDate(to.toDate())
                            }}
                        >
                            {s.name}
                        </Button>
                    ))}
                </HStack>
                <VStack align="flex-start" gap={1}>
                    <HStack gap={4}>
                        <p style={{ fontSize: 12 }}>
                            {totals
                                ? Object.entries(totals)
                                      .map(([key, value]) => (value > 0 ? `${key}: ${value}` : ``))
                                      .join(' ')
                                : 'No totals available'}
                        </p>
                    </HStack>
                    <HStack gap={4}>
                        {allTypes.map((type) => (
                            <Checkbox.Root
                                key={type}
                                checked={selectedTypes.includes(type)}
                                onCheckedChange={({ checked }) =>
                                    setSelectedTypes((current) =>
                                        checked
                                            ? [...new Set([...current, type])]
                                            : current.filter((t) => t !== type)
                                    )
                                }
                            >
                                <Checkbox.HiddenInput />
                                <Checkbox.Control />
                                <Checkbox.Label>{type}</Checkbox.Label>
                            </Checkbox.Root>
                        ))}
                    </HStack>
                </VStack>
            </HStack>

            <Box style={{ width: '100%', height: '100%' }}>
                <ViewMap
                    initialCenter={{ lng: 55.39040044884541, lat: 43.17517749347715 }}
                    initialZoom={0.68}
                    onChange={setChange}
                    data={data}
                />
            </Box>
            {
                //JSON.stringify(test)
            }
        </Box>
    )
}
